class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-link');

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Olive Bird: add recommended products without leaving the cart drawer.
if (!customElements.get('ob-cart-upsell')) {
  customElements.define('ob-cart-upsell', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const button = this.querySelector('button');
      const choice = this.querySelector('select, input');
      if (!button || !choice) return;
      const card = this.closest('.ob-cart-suggestion, .cart-items--recommendations-list-item');
      const image = card && card.querySelector('.ob-cart-suggestion-image img, .cart-items--recommendations-list-item-image img');
      const originalImage = image && {src: image.getAttribute('src'), srcset: image.getAttribute('srcset'), alt: image.getAttribute('alt')};
      this.addEventListener('change', event => {
        event.stopPropagation();
        button.disabled = this.busy || !choice.value;
        const selected = choice.selectedOptions && choice.selectedOptions[0];
        if (!image || !selected) return;
        if (choice.value && selected.dataset.image) {
          image.removeAttribute('srcset');
          image.src = selected.dataset.image;
          image.alt = selected.dataset.imageAlt || originalImage.alt || '';
        } else {
          image.src = originalImage.src;
          if (originalImage.srcset) image.setAttribute('srcset', originalImage.srcset);
          else image.removeAttribute('srcset');
          image.alt = originalImage.alt || '';
        }
      });
      button.addEventListener('click', async event => {
        event.preventDefault();
        event.stopPropagation();
        if (this.busy || !choice.value) return;
        this.busy = true;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        const error = this.querySelector('[role="alert"]');
        error.textContent = '';
        const drawer = this.closest('cart-drawer');
        try {
          const response = await fetch(this.dataset.addUrl, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
            body: JSON.stringify({id: choice.value, quantity: 1, sections: drawer ? drawer.getSectionsToRender().map(s => s.id) : [], sections_url: window.location.pathname})
          });
          const data = await response.json();
          if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this item. Please try again.');
          if (!drawer) { window.location.reload(); return; }
          // A successful add may omit bundled sections. Refresh the display without adding again.
          const requiredSections = drawer.getSectionsToRender().map(section => section.id);
          if (!data.sections || requiredSections.some(id => !data.sections[id])) {
            try {
              const sectionUrl = new URL(window.location.href);
              sectionUrl.searchParams.set('sections', requiredSections.join(','));
              const sectionResponse = await fetch(sectionUrl.toString(), {headers: {'Accept': 'application/json'}});
              if (sectionResponse.ok) data.sections = await sectionResponse.json();
            } catch (_) { /* The item is already in the bag; never repeat the add. */ }
          }
          if (!data.sections || requiredSections.some(id => !data.sections[id])) {
            window.location.assign(window.routes && window.routes.cart_url || '/cart');
            return;
          }
          drawer.classList.remove('is-empty');
          drawer.renderContents(data);
          if (typeof initCartRecommendationsArrows === 'function') initCartRecommendationsArrows();
        } catch (problem) {
          error.textContent = problem.message || 'Unable to add this item. Please try again.';
        } finally {
          this.busy = false;
          button.disabled = !choice.value;
          button.removeAttribute('aria-busy');
        }
      });
    }
  });
}
